import React, { useMemo } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import {
  ArrowBack,
  DescriptionOutlined,
  DownloadOutlined,
  PrintOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStatsSettings } from '../context/Context';

const ReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { healthData, selectedStats } = useStatsSettings();

  const visibleMetrics = useMemo(
    () =>
      metrics.filter((metric) => selectedStats[metric.key]).map((metric) => ({
        ...metric,
        value: healthData[metric.key],
      })),
    [healthData, selectedStats],
  );

  const handleDownload = () => {
    const report = {
      app: 'SeeVita',
      generatedAt: new Date().toISOString(),
      metrics: visibleMetrics.map(({ label, value, unit }) => ({ label, value, unit })),
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seevita-report-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 58%, #102040 100%)',
        color: 'white',
        px: { xs: 2, md: 5 },
        py: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.58)', textTransform: 'none', fontWeight: 700, px: 0, mb: 3 }}
        >
          Back to dashboard
        </Button>

        <Box sx={reportPanelSx}>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '3px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.48)' }}>
                SeeVita Report
              </Typography>
              <Typography sx={{ mt: 1, fontSize: { xs: '2rem', md: '2.6rem' }, lineHeight: 1.05, fontWeight: 800 }}>
                Latest vital summary
              </Typography>
              <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                Generated {new Date().toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<PrintOutlined />} onClick={() => window.print()} sx={outlineButtonSx}>
                Print
              </Button>
              <Button variant="contained" startIcon={<DownloadOutlined />} onClick={handleDownload} sx={solidButtonSx}>
                Export
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.08)' }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            {visibleMetrics.map((metric) => (
              <Box key={metric.key} sx={metricCardSx}>
                <Typography sx={{ color: 'rgba(255,255,255,0.44)', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.2px' }}>
                  {metric.label}
                </Typography>
                <Typography sx={{ color: metric.color, fontSize: { xs: '1.7rem', md: '2rem' }, fontWeight: 800, mt: 1 }}>
                  {metric.value}
                </Typography>
                <Typography sx={{ color: metric.color, opacity: 0.72, fontSize: '0.8rem', fontWeight: 700 }}>
                  {metric.unit}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 3, p: 2, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <DescriptionOutlined sx={{ color: '#93c5fd', mt: 0.2 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontSize: '0.88rem', lineHeight: 1.7 }}>
              This summary reflects the latest values available in the current session. Export the JSON file for record keeping or print this view for a quick clinical handoff.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type MetricKey =
  | 'heartRate'
  | 'systolicBP'
  | 'diastolicBP'
  | 'respiratoryRate'
  | 'hrv';

const metrics: Array<{ key: MetricKey; label: string; unit: string; color: string }> = [
  { key: 'heartRate', label: 'Heart rate', unit: 'BPM', color: '#f87171' },
  { key: 'systolicBP', label: 'Systolic', unit: 'mmHg', color: '#60a5fa' },
  { key: 'diastolicBP', label: 'Diastolic', unit: 'mmHg', color: '#a78bfa' },
  { key: 'respiratoryRate', label: 'Resp. rate', unit: '/min', color: '#2dd4bf' },
  { key: 'hrv', label: 'HRV', unit: 'ms', color: '#818cf8' },
];

const reportPanelSx = {
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '18px',
  bgcolor: 'rgba(10,28,60,0.58)',
  boxShadow: '0 16px 50px rgba(0,0,0,0.22)',
  p: { xs: 2.5, md: 3 },
};

const metricCardSx = {
  borderRadius: '12px',
  bgcolor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  p: 2,
};

const outlineButtonSx = {
  color: 'rgba(255,255,255,0.82)',
  borderColor: 'rgba(255,255,255,0.16)',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 800,
  '&:hover': { borderColor: 'rgba(255,255,255,0.36)', bgcolor: 'rgba(255,255,255,0.06)' },
};

const solidButtonSx = {
  color: '#0a1628',
  bgcolor: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 800,
  '&:hover': { bgcolor: '#e8f0fe' },
};

export default ReportPage;
